import { Fragment, useContext, useEffect, useState } from "react";
import { toStdDate } from '../../../utils/dates';
import { useLazyGetRequestsMetricsQuery } from "../../../services/swarmsApi";
import { useLazyGetEventsQuery } from "../../../services/eventApi";
import { Skeleton, Box, Button, Modal, Popover, Fade, Paper, Typography, Avatar } from "@mui/material";
import { STN_FE_URL } from '../../../constants';
import { REQEST_ACTIVITY_GRID_COLUMN } from "./RequstActivityGridColumn";
import { DataGridPremium, GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useGetUserStyleOrDefaultQuery, useUpdateColumnsForUserMutation } from "../../../services/tableStyleApi";
import { ApplicationTable } from "../../../constants/enums";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useGetHistoryRequestActivityQuery, useLazyGetRequestActivityQuery, useRequestActivityHideEventsMutation, useRequestActivityResetActivityMutation, useRequestActivityUnhideAllEventsMutation } from "../../../services/requestActivityApi";
import { selectAllBuyManagers } from "../../../store/slices/buyManager/buyManagers";
import { useSelector } from "react-redux";
import { Tooltip } from '@mui/material';
import SignalREncorehiveContext from "../../../context/signalREncorehive";

const formatEventDate = (value) => {
  const options = { weekday: "short", month: "2-digit", day: "2-digit", year: "2-digit", hour: "numeric", hour12: true };
  const formattedDate = value.toLocaleString("en-US", options)
    .replace(",", "") // Remove the comma after the weekday
    .replace(/,\s/g, " ") // Remove the comma after the date
    .replace(/\//g, "-") // Replace '/' with '-'
    .replace(/\b0/g, ""); // Remove leading zeros from the date

  const dayMapping = {
    "Sun": "Su",
    "Mon": "Mo",
    "Tue": "Tu",
    "Wed": "We",
    "Thu": "Th",
    "Fri": "Fr",
    "Sat": "Sa"
  };

  const formattedDatePart = dayMapping[formattedDate.split(" ")[0]]; // Get the abbreviated day name

  return `${formattedDatePart} ${formattedDate.substring(formattedDate.indexOf(" ") + 1)}`;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 430,
  height: 150,
  padding: '2.5em',
  bgcolor: 'background.paper',
  borderRadius: '1em',
  boxShadow: 24,
};

const EventInfoStyles = {
  bgcolor: 'background.paper',
  borderRadius: '1em',
  padding: '1em',
  width: 450,
}

const EventInfoPopper = ({ open, anchorEl, eventInfo, onClose }) => {
  const { data: requestActivityHistory, isLoading: isLoading } = useGetHistoryRequestActivityQuery(eventInfo?.swarmId, { skip: !eventInfo })

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      PaperProps={{
        onMouseLeave: onClose,
        style: EventInfoStyles
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "flex-start" }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ConfirmationNumberOutlinedIcon color='primary' sx={{ margin: '0 0.25em 0 0' }} />
              <span>{eventInfo?.eventName}</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FmdGoodOutlinedIcon color='primary' sx={{ margin: '0 0.25em 0 0' }} />
              <span>{eventInfo?.venueName}</span>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '150px' }}>
            <EventOutlinedIcon color='primary' sx={{ margin: '0 0.25em 0 0' }} />
            {eventInfo?.eventDate ? formatEventDate(new Date(eventInfo?.eventDate)) : null}
          </Box>
        </Box>
        <div className="divisor" style={{ margin: "15px 0 15px 0" }}></div>
        {!isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <Box sx={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 'bold' }}>PURCHASED</p>
              <p>{requestActivityHistory?.completed?.tickets}</p>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 'bold' }}>PASSED</p>
              <p>{requestActivityHistory?.rejected?.tickets}</p>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 'bold' }}>LOST</p>
              <p>{requestActivityHistory?.lost?.tickets}</p>
            </Box>
          </Box>)}
      </Box>
    </Popover>
  )
}

const RequestActivity = (props) => {
  const [getMetrics, { data, isLoading }] = useLazyGetRequestActivityQuery();
  const [getEvents] = useLazyGetEventsQuery();
  const buyManagers = useSelector(selectAllBuyManagers);

  const [isModalOpened, setIsModalOpened] = useState(false);

  const [isEventInfoOpened, setIsEventInfoOpened] = useState(false);
  const [rowAnchorEl, setRowAnchorEl] = useState(null);
  const [selectedEventInfo, setSelectedEventInfo] = useState(null);

  const { signalREncorehive: signalRConnection  } = useContext(SignalREncorehiveContext);

  const [events, setEvents] = useState([]);

  const [rows, setRows] = useState([]);

  const [columns, setColums] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const { data: styles } = useGetUserStyleOrDefaultQuery(ApplicationTable.RequestHistory);
  const [updateColumnsForUser] = useUpdateColumnsForUserMutation();
  const [gridStyle, setGridStyle] = useState(null);
  const [isFirstGridLoad, setIsFirstGridLoad] = useState(true);

  const [resetActivity] = useRequestActivityResetActivityMutation();
  const [unhideEvents] = useRequestActivityUnhideAllEventsMutation();
  const [hideEvents] = useRequestActivityHideEventsMutation();

  useEffect(() => {
    getMetrics()
  }, []);

  useEffect(() => {
    // meybe we can impove this to avoid doing a request every time we want to update the metrics
    signalRConnection?.on('EditBuyReq', (data) => {
      getMetrics()
    })
    signalRConnection?.on('EditSwarmEvent', (data) => {
      getMetrics()
    })
    signalRConnection?.on('CreateSwarmEvent', (data) => {
      getMetrics()
    })
    signalRConnection?.on('NewSwarmPurchase', (data) => {
      getMetrics()
    })

  }, [signalRConnection])

  useEffect(() => {
    if (!styles)
      return;

    setGridStyle([...styles]);
  }, [styles])

  useEffect(async () => {
    if (!styles || !gridStyle)
      return;

    let displayColumns = [];
    let hideColumns = {};

    const sortedColumns = [...gridStyle].sort((a, b) => a.index - b.index);
    sortedColumns.forEach(column => {
      let displayColumn = gridColumns[column.name];
      
      if(!displayColumn){
        console.error("Column Not Found, column: ", column, " gridColumns: ", gridColumns);
      } else {        
        if (column.width) {
          displayColumn.flex = 0;
          displayColumn.width = column.width;
        }

        hideColumns[displayColumn.field] = column.mustBeVisible;
        displayColumns.push(displayColumn);
      }
    });

    setColumnVisibilityModel(hideColumns);
    setColums(displayColumns);

    if (!isFirstGridLoad) {
      await updateColumnsForUser({ table: ApplicationTable.RequestHistory, columns: gridStyle }).unwrap();
    }
    setIsFirstGridLoad(false);
  }, [gridStyle])

  useEffect(() => {
    const rows = data?.swarmsResume?.map(m => mapDataToRow(m, events, buyManagers)) || [];
    setRows(rows);
  }, [data, events, buyManagers])

  useEffect(async () => {
    if (!data || data?.swarmsResume.length === 0)
      return;

    const query = data?.swarmsResume?.map(swarm => `bteId=${swarm.eventId}`).join('&');

    try {
      const res = await getEvents(`?${query}`).unwrap();
      const events = res.map(e => e.eventModel);

      setEvents(events);
    } catch (error) {
      if (error.status === 401) return;
      throw error;
    }
  }, [data])

  const mapDataToRow = (metric, events, buyManagers) => {
    const event = events?.find(e => e?.bteId === metric.eventId);

    const eventName = event?.name;
    const venueName = !event?.venue?.name ? null : event?.venue?.name + ', ' + event?.venue?.city;
    const eventDate = event?.date;

    return {
      id: metric.id,
      eventInfo: event ? { swarmId: metric.id, eventId: metric.eventId, eventName: eventName, venueName: venueName, eventDate: eventDate } : null,
      event: eventName,
      venue: venueName,
      date: eventDate,
      isNow: metric.isNow,
      modifiedOn: metric.modifiedOn,
      salesDate: metric.salesDate,
      completed: metric.completed,
      rejected: metric.rejected,
      lost: metric.lost,
      accepted: metric.accepted,

      bteId: metric.eventId,
      buyManager: buyManagers?.find( BM => BM.buyManagerID === metric.buyManagerId )
    }
  }

  const EventDateCell = params => {
    if (!params.value) {
      return <Skeleton variant="text" width="70%" />;
    }

    const date = new Date(params.value);
    return formatEventDate(date);
  }

  const onEventInfoClick = (e, eventInfo) => {
    setRowAnchorEl(e.currentTarget);
    setSelectedEventInfo(eventInfo);
    setIsEventInfoOpened(true);
  }

  const onEventInfoClosed = () => {
    setIsEventInfoOpened(false);
    setRowAnchorEl(null);
    setSelectedEventInfo(null);
  }

  const onHideEventClicked = async (e, info) => {
    try {
      await hideEvents([info]).unwrap();
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    }
  }

  const onShowHideEventClicked = async (e) => {
    try {
      await unhideEvents().unwrap();
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    }
  }

  const gridColumns = {
    [REQEST_ACTIVITY_GRID_COLUMN.ID]: { field: "id", hide: true, flex: 0, aggregable: false, },
    [REQEST_ACTIVITY_GRID_COLUMN.HIDE_EVENT]: {
      field: "hideEvent",
      type: 'actions',
      width: 25,
      minWidth: 20,
      aggregable: false,
      getActions: (params) => {
        if (params.row.id)
          return [
            <GridActionsCellItem
              icon={<VisibilityOffOutlinedIcon sx={{ fontSize: '1em', color: 'var(--orange)' }} />}
              onClick={x => onHideEventClicked(x, params.row.id)}
              label="Hide event"
            />
          ]
        else
          return []
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.EVENT_INFO]: {
      field: "eventInfo",
      type: 'actions',
      width: 25,
      minWidth: 20,
      aggregable: false,
      getActions: (params) => {
        if (params.row.eventInfo)
          return [
            <GridActionsCellItem
              icon={<InfoOutlinedIcon sx={{ fontSize: '1em', color: 'var(--orange)' }} />}
              onClick={x => onEventInfoClick(x, params.row.eventInfo)}
              label="Swarm total info"
            />
          ]
        else
          return []
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.BUYMANAGER]: {
      headerName: "BUY MANAGER",
      field: "buyManager",
      flex: 1,
      aggregable: false,
      align:"center",
      renderCell: (params) => {
        if(!params.value) return
        const avatar = {color: params.value?.color ||'#FFB74D', initials: Array.from(params.value?.firstName[0]) + Array.from(params.value?.lastName)[0]}
        return (
          <Tooltip title={`${params.value?.firstName} ${params.value?.lastName}`}>
            <Avatar sx={{ backgroundColor: avatar?.color || '#FFB74D', height:"30px", width:"30px", fontSize:"12px", fontWeight:"700" }}>
              {avatar.initials}
            </Avatar>
          </Tooltip>
        );
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.EVENT]: {
      headerName: "EVENT",
      field: "event",
      flex: 4,
      aggregable: false,
      renderCell: (params) => {
        if (params?.rowNode?.type === 'pinnedRow')
          return <>
            <span style={{ color: 'var(--orange)', fontWeight: '600', fontSize: '12px', textTransform:'uppercase' }}>Request Total Summary</span>
          </>;

        if (!params.value)
          return <Skeleton variant="text" width="100%" />
        else {
          let baseURL = STN_FE_URL
          baseURL += '#/events-details';
          return params.row.bteId
            ?
            <a href={`${baseURL}/${params.row.bteId}`} target="_blank" rel="noreferrer">{params.value}</a>
            :
            params.value
        }
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.VENUE]: {
      headerName: "VENUE",
      field: "venue",
      flex: 4,
      aggregable: false,
      renderCell: v => {
        if (v?.rowNode?.type === 'pinnedRow')
          return <>
          </>;

        return !v.value ? <Skeleton variant="text" width="100%" /> : v.value
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.EVENT_DATE]: {
      headerName: "EVENT DATE",
      field: "date",
      flex: 2,
      aggregable: false,
      renderCell: (params) => {
        if (params?.rowNode?.type === 'pinnedRow')
          return <>
          </>;

        return EventDateCell(params)
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.SALE_DATE]: {
      headerName: "SALE DATE",
      field: "saleDate",
      flex: 2,
      aggregable: false,
      valueGetter: v => {
        if (v?.rowNode?.type === 'pinnedRow')
          return null;

        const date = v.row.isNow === true && v.row.modifiedOn !== null
          ? toStdDate(v.row.modifiedOn + "Z")
          : toStdDate(v.row.salesDate + "Z");
        return date;
      }
    },
    [REQEST_ACTIVITY_GRID_COLUMN.PURCHASED]: { headerName: "PURCHASED", field: "completed", flex: 1, type: 'number', headerAlign: 'center', align: 'center', valueGetter: (v) => v.value?.tickets },
    [REQEST_ACTIVITY_GRID_COLUMN.PASSED]: { headerName: "PASSED", field: "rejected", type: 'number', flex: 1, headerAlign: 'center', align: 'center', valueGetter: (v) => v.value?.tickets },
    [REQEST_ACTIVITY_GRID_COLUMN.LOST]: { headerName: "LOST", field: "lost", flex: 1, type: 'number', headerAlign: 'center', align: 'center', valueGetter: (v) => v.value?.tickets },
    [REQEST_ACTIVITY_GRID_COLUMN.PENDING]: { headerName: "PENDING", field: "accepted", type: 'number', flex: 1, headerAlign: 'center', align: 'center', valueGetter: (v) => v.value?.tickets }
  }

  const onColumnVisibilityModelChange = (newModel) => {
    if (Object.keys(newModel).length === 0)
      Object.entries(gridColumns).forEach(([key, value]) => newModel[value.field] = true)

    let newStyle = gridStyle.map(style => {
      let gridRow = gridColumns[style.name]

      const [key, value] = Object.entries(newModel).find(([key, column]) => key === gridRow.field) || [];

      return {
        ...style,
        mustBeVisible: key ? value : style.mustBeVisible
      }
    });

    setGridStyle(newStyle);
  }

  const onColumnWidthChanged = ({ colDef, element, width }) => {
    const [key] = Object.entries(gridColumns).find(([key, column]) => column.field === colDef.field)

    let newStyle = gridStyle.map(style => {
      const isSelectedStyle = style.name === key

      return {
        ...style,
        width: isSelectedStyle ? Math.round(width) : style.width
      }
    });

    setGridStyle(newStyle);
  }

  const onColumnOrderChange = ({ column, oldIndex, targetIndex }) => {
    const [key] = Object.entries(gridColumns).find(([key, col]) => col.field === column.field)

    ++targetIndex; ++oldIndex;
    let newStyle = gridStyle.map(style => {
      const isSelectedStyle = style.name === key;
      const isPrevColumn = style.index === targetIndex;
      const styleIndex = style.index;

      if (isSelectedStyle) {
        return {
          ...style, index: targetIndex
        }
      } else {
        if (oldIndex < targetIndex && styleIndex > oldIndex && styleIndex <= targetIndex) {
          return {
            ...style, index: styleIndex - 1
          }
        } else if (oldIndex > targetIndex && styleIndex < oldIndex && styleIndex >= targetIndex) {
          return {
            ...style, index: styleIndex + 1
          }
        } else {
          return {
            ...style
          }
        }
      }
    });

    setGridStyle(newStyle);
  }

  const onRowSelectionModelChange = (x) => {
    setRowSelectionModel(x);
  }

  const onModalClose = () => {
    setIsModalOpened(false)
  }

  const onClearActivitySubmit = async () => {
    try {
      await resetActivity([...rowSelectionModel]).unwrap()
      setRowSelectionModel([]);
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    } finally {
      onModalClose();
    }
  }

  return (
    <Fragment>
      {isModalOpened && (
        <Modal
          open={isModalOpened}
          onClose={onModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box>
                <p style={{ textAlign: 'center' }}>Are you sure want to clear the Reqest Activiry for {(rowSelectionModel?.length || 0) > 0 ? `${rowSelectionModel?.length}` : 'all'} {(rowSelectionModel?.length || 0) === 1 ? 'event' : 'events'}?</p>
              </Box>
              <Box>
                <Button variant='contained' color='secondary' name="cancelButton" sx={{ minWidth: '100px', marginRight: '15px' }} onClick={onModalClose}>Cancel</Button>
                <Button variant='contained' type="submit" name="acceptButton" color='primary' sx={{ minWidth: '100px' }} onClick={onClearActivitySubmit}>OK</Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}

      <EventInfoPopper
        open={isEventInfoOpened}
        anchorEl={rowAnchorEl}
        eventInfo={selectedEventInfo}
        onClose={onEventInfoClosed}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <h4 style={{ margin: "0 0 0.5em 0", fontWeight: '600' }}>Request Activity</h4>
        <Box>
          <Button variant="outlined" sx={{ marginRight: '15px' }} onClick={onShowHideEventClicked}>Show hidden events</Button>
          <Button variant="outlined" onClick={x => setIsModalOpened(true)}>Clear Activity</Button>
        </Box>
      </Box>
      <DataGridPremium
        headerHeight={40}
        rowHeight={36}
        loading={isLoading}
        sx={{
          height: "30vh",
          borderRadius: "1em",
          backgroundColor: "white",
          padding: "0 0.5em 0.5em 0.5em",
          '&:disabled': { backgroundColor: "red" },
          zIndex: 0
        }}

        columns={columns}
        rows={rows}
        columnVisibilityModel={columnVisibilityModel}
        getRowId={r => r.id}

        hideFooter
        disableColumnPinning
        checkboxSelection={false}
        disableRowGrouping={true}

        initialState={{
          aggregation: {
            model: {
              completed: 'sum',
              rejected: 'sum',
              lost: 'sum',
              accepted: 'sum'
            }
          }
        }}

        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}

        onColumnWidthChange={onColumnWidthChanged}
        onColumnOrderChange={onColumnOrderChange}
        onColumnVisibilityModelChange={(newModel) => onColumnVisibilityModelChange(newModel)}
      />

    </Fragment>
  );
}

export default RequestActivity;
